import { useEffect, useRef, useState } from 'react';
import styles from '../GoalsList.module.scss';
import Trash from '../../../assets/icons/Trash_Empty.svg';
import EditPencil from '../../../assets/icons/Edit_Pencil_Line_02.svg';
import ExternalLink from '../../../assets/icons/External_Link.svg';

import { useGoalActions } from './useGoalActions';
import { LimitMeta } from './LimitMeta';
import { DefaultTerm } from './DefaultTerm';
import { IncomeActions } from './IncomeActions';
import { SaveActions } from './SaveActions';

export default function GoalItem({
  goal,
  active,
  onToggle,
  onDeleted,
  onUpdated,
}) {
  const itemRef = useRef(null);

  const isSave = goal.kind === 'save';
  const isLimit = goal.kind === 'limit';
  const isIncome = goal.kind === 'income';

  const progress = Number(goal.progress) || 0;

  const saveReached = isSave && Number(goal.current) >= Number(goal.target);
  const incomeReached = isIncome && !!goal.canFix;

  const showReachedLabel = saveReached || incomeReached;
  const showIncomeActions = incomeReached;

  const [topupOpen, setTopupOpen] = useState(false);
  const [topupValue, setTopupValue] = useState('');

  const [incomeJustFixed, setIncomeJustFixed] = useState(false);
  const [saveJustDone, setSaveJustDone] = useState(false);

  const kindMod =
    goal.kind === 'save'
      ? styles['goalCard--save']
      : goal.kind === 'limit'
      ? styles['goalCard--limit']
      : goal.kind === 'income'
      ? styles['goalCard--income']
      : '';

  const imageUrl = (goal.imageUrl || goal.image_url || '').trim();
  const productUrl = (goal.productUrl || goal.product_url || '').trim();

  const hasImage = goal.kind === 'save' && !!imageUrl;
  const hasLink = goal.kind === 'save' && !!productUrl;

  const { busy, setBusy, msg, flash, refetchMe, post, patch } = useGoalActions(
    goal.id,
    onUpdated
  );

  const [saveBusy, setSaveBusy] = useState(false);
  const showSaveFinalAction = saveReached;

  const hideEditDelete =
    (isIncome && showIncomeActions) || (isSave && showSaveFinalAction);
  const hideActions = showIncomeActions && !incomeJustFixed;

  const progressMod = isLimit
    ? progress >= 100
      ? styles['goalCard__progressFill--red']
      : progress >= 80
      ? styles['goalCard__progressFill--yellow']
      : styles['goalCard__progressFill--green']
    : isSave || isIncome
    ? progress >= 100
      ? styles['goalCard__progressFill--green']
      : styles['goalCard__progressFill--blue']
    : styles['goalCard__progressFill--green'];

  useEffect(() => {
    if (!topupOpen) return;

    const onDocDown = (e) => {
      if (!itemRef.current) return;
      if (!itemRef.current.contains(e.target)) submitTopup();
    };

    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [topupOpen, topupValue]);

  const openTopup = (e) => {
    e.stopPropagation();
    setTopupOpen(true);
    setTopupValue('');
  };

  const closeTopup = () => {
    setTopupOpen(false);
    setTopupValue('');
  };

  async function submitTopup() {
    if (!topupOpen) return;

    const amt = Number(topupValue);
    closeTopup();
    if (!Number.isFinite(amt) || amt <= 0) return;

    try {
      await post(`/api/goals/${goal.id}/save-topup`, {
        amount: Math.round(amt),
      });
      await refetchMe();
    } catch (e) {}
  }

  async function handleDelete(e) {
    e.stopPropagation();
    const ok = window.confirm('Удалить цель?');
    if (!ok) return;

    const res = await fetch(`/api/goals/${goal.id}`, {
      method: 'DELETE',
    });
    if (!res.ok) return;
    onDeleted?.(goal.id);
  }

  async function incomeFix(e) {
    e.stopPropagation();
    if (busy || incomeJustFixed) return;

    setBusy(true);
    try {
      await post(`/api/goals/${goal.id}/income-fix`, {});
      setIncomeJustFixed(true);
      setTimeout(() => onUpdated?.({ id: goal.id, removed: true }), 2000);
    } catch {
    } finally {
      setBusy(false);
    }
  }

  async function incomeExtend(percent) {
    if (busy) return;

    setBusy(true);
    try {
      await post(`/api/goals/${goal.id}/income-extend`, { percent });
      flash(`Цель +${percent}%`);
      await refetchMe();
    } catch (e) {
      flash(e?.message || 'Ошибка');
    } finally {
      setBusy(false);
    }
  }

  async function saveFinish(e) {
    e.stopPropagation();
    if (saveBusy || saveJustDone) return;

    setSaveBusy(true);
    try {
      const ok = await post(`/api/goals/${goal.id}/save-finish`, {});
      if (!ok) return;
      setSaveJustDone(true);
      setTimeout(() => onUpdated?.({ id: goal.id, removed: true }), 2000);
    } catch {
    } finally {
      setSaveBusy(false);
    }
  }

  return (
    <div
      ref={itemRef}
      className={[
        styles.goalCard,
        kindMod,
        active ? styles['goalCard--active'] : '',
        incomeReached ? styles['goalCard--incomeReached'] : '',
        hasImage ? styles['goalCard--hasImage'] : '',
        hasLink ? styles['goalCard--hasLink'] : '',
      ].join(' ')}
      onClick={(e) => {
        if (e.target.closest('button, a, input, textarea, select, label')) return;
        onToggle?.();
      }}>
      {showReachedLabel ? (
        <div className={styles.goalCard__labelWrap}>
          <div className={styles.goalCard__label}>Достигнуто</div>
        </div>
      ) : null}

      <div className={styles.goalCard__top}>
        {hasImage ? (
          <div className={styles.goalCard__imgWrap}>
            <img
              src={imageUrl}
              alt=''
              className={styles.goalCard__img}
              loading='lazy'
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        ) : null}

        <div className={styles.goalCard__main}>
          <div className={styles.goalCard__header}>
            <h4 className={styles.goalCard__title}>{goal.title}</h4>

            {productUrl ? (
              <a
                href={productUrl}
                target='_blank'
                rel='noreferrer'
                className={styles.goalCard__link}
                onClick={(e) => e.stopPropagation()}>
                <img src={ExternalLink} alt="" className={styles['goalCard__icon--open']}/>
              </a>
            ) : null}
          </div>

          <div className={styles.goalCard__values}>
            <h4 className={styles.goalCard__current}>{goal.currentText}</h4>
            <h4 className={styles.goalCard__target}>{goal.targetText}</h4>
          </div>

          <div className={styles.goalCard__progress}>
            <div
              className={[styles.goalCard__progressFill, progressMod].join(' ')}
              style={{ width: `${Math.min(progress, 100)}%` }}
            />
          </div>
        </div>
      </div>

      <div className={styles.goalCard__bottom}>
        <div className={styles.goalCard__content}>
          {goal.description ? (
            <p className={styles.goalCard__description}>{goal.description}</p>
          ) : null}

          {isLimit ? (
            <LimitMeta goal={goal} styles={styles} />
          ) : (
            <DefaultTerm goal={goal} styles={styles} />
          )}

          {showIncomeActions ? (
            <IncomeActions
              goal={goal}
              styles={styles}
              busy={busy}
              msg={msg}
              incomeJustFixed={incomeJustFixed}
              onFix={incomeFix}
              onExtend={incomeExtend}
            />
          ) : null}

          {!hideActions ? (
            <div
              className={[
                styles.goalCard__actions,
                isSave
                  ? styles['goalCard__actions--save']
                  : styles['goalCard__actions--default'],
              ].join(' ')}>
              {isSave ? (
                <SaveActions
                  styles={styles}
                  topupOpen={topupOpen}
                  topupValue={topupValue}
                  setTopupValue={setTopupValue}
                  openTopup={openTopup}
                  submitTopup={submitTopup}
                  showSaveFinalAction={showSaveFinalAction}
                  saveJustDone={saveJustDone}
                  saveBusy={saveBusy}
                  onFinish={saveFinish}
                  onCloseTopup={closeTopup}
                />
              ) : null}

              {!hideEditDelete ? (
                <>
                  <button
                    type='button'
                    className={`${styles.goalCard__btn} ${styles['goalCard__btn--edit']}`}
                    onClick={(e) => e.stopPropagation()}
                    aria-label='Изменить'>
                    <img
                      src={EditPencil}
                      alt=''
                      className={styles.goalCard__icon}
                    />
                    {!isSave ? (
                      <span className={styles.goalCard__btnText}>Изменить</span>
                    ) : null}
                  </button>

                  <button
                    type='button'
                    className={`${styles.goalCard__btn} ${styles['goalCard__btn--del']}`}
                    onClick={handleDelete}
                    aria-label='Удалить'>
                    <img src={Trash} alt='' className={styles.goalCard__icon} />
                    {!isSave ? (
                      <span className={styles.goalCard__btnText}>Удалить</span>
                    ) : null}
                  </button>
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}